import { globals } from './globals';

/** The minimum framerate we will accept before deciding that performance is bad */
const MINIMUM_FRAMERATE = 35;
/** How long to count frames for (in seconds) before doing a performance check. */
const BATCH_DURATION = 3;
/** Number of frames to count before doing a "how is performance doing" check. */
const FRAMES_TO_COUNT = MINIMUM_FRAMERATE * BATCH_DURATION;
/**
 * Maximum allowable time (in milliseconds) for the specified number of frames to happen.
 * If it takes longer, then we say the device is low performance and lower settings.
 */
const MAX_TIME = BATCH_DURATION * 1000;

export class FramerateTracker
{
    /** Time of last frame (ms), for calculating elapsed. */
    private last: number = 0;
    /** Number of frames counted in order to handle our calculation */
    private frameCount: number = 0;
    /** Running average frame time, pre-divided by the number of frames that we will count */
    private totalFrameTime: number = 0;
    /** If we should be tracking at the current time */
    private _track:boolean = false;

    /**
     * If we should be tracking at the current time. Allows disabling during loading and other
     * planned periods of poor performance
     */
    public get track() { return this._track; }
    public set track(track)
    {
        this._track = track;
        if (!track)
        {
            this.frameCount = 0;
            this.totalFrameTime = 0;
        }
    }

    /**
     * Starts tracking after a short delay, to allow framerate to settle down after
     * loading & setup completes.
     */
    public trackWithDelay(msDelay: number)
    {
        setTimeout(() => this.track = true, msDelay);
    }

    /**
     * Reset last time value (to recover from paused state)
     */
    public resetLast()
    {
        this.last = performance.now();
    }

    /**
     * Runs an update, updating performance settings if needed.
     * @return Elapsed time from the previous frame, in milliseconds.
     */
    public update(): number
    {
        const now = performance.now();
        if (!this.last)
        {
            this.last = now;
            return 0;
        }
        const elapsed = now - this.last;
        this.last = now;

        // only do things if enabled and not already on low performance
        // (once you go low, we don't trust the device to stay stably high later. */
        if (this._track && !globals.LOW_PERFORMANCE.value)
        {
            this.totalFrameTime += elapsed;
            if (++this.frameCount >= FRAMES_TO_COUNT)
            {
                // now that we've tracked frames for a bit, see if it took too long
                if (this.totalFrameTime > MAX_TIME)
                {
                    globals.LOW_PERFORMANCE.value = true;
                }
                // reset counters
                this.frameCount = 0;
                this.totalFrameTime = 0;
            }
        }
        return elapsed;
    }
}